import React, {useContext, useMemo} from 'react';

import {WorkspaceOwnershipFilterLabel} from 'src/modules/profile/user/tabs/content/workspace/util';
import {WorkspaceDetails} from 'src/openapi/admin/models/WorkspaceDetails';
import {WorkspaceGeneralInfo} from 'src/openapi/admin/models/WorkspaceGeneralInfo';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import {FlatButton} from '@pb/kit-component-button';
import {Popover, PopoverHeader} from '@pb/kit-component-dropdown';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {FormattedDate} from '@pb/kit-component-formatters';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {Cell, Row} from '@pb/kit-component-table';
import {LocaleContext} from '@pb/kit-context-locale';

import {WorkspaceDropdownContent} from './workspace-dropdown-content';

type Props = {
    showZebra: boolean,
    workspaceId: number,
    workspace: WorkspaceGeneralInfo,
    workspaceDetails?: WorkspaceDetails,
    userId: number,
};

export const WorkspaceTableRow = (props: Props) => {
    const {showZebra, workspaceId, workspaceDetails, workspace, userId} = props;
    const {locale} = useContext(LocaleContext);

    const lastUpdated = useMemo(() => {
        const ts = workspaceDetails?.lastModifiedAt;
        if (!ts) {
            return '--';
        }
        return (
            FormattedDate.format({
                value: ts,
                format: mapDateFormat(locale.dateFormat, 'WORD'),
            }) || ''
        );
    }, [workspaceDetails?.lastModifiedAt, locale.dateFormat]);

    const created = useMemo(() => {
        const ts = workspaceDetails?.createdAt;
        if (!ts) {
            return '--';
        }
        return (
            FormattedDate.format({
                value: ts,
                format: mapDateFormat(locale.dateFormat, 'WORD'),
            }) || ''
        );
    }, [workspaceDetails?.createdAt, locale.dateFormat]);

    return (
        <Row showZebra={showZebra} key={workspaceId}>
            <Cell>
                <Popover
                    hoverEnabled={true}
                    placement="right"
                    horizontalOffset={50}
                    maxWidth={320}
                    anchor={({anchorRef, getAnchorProps}) => (
                        <span ref={anchorRef} {...getAnchorProps()}>
                            <EllipsisTextHint title={workspace.workspaceName}>
                                <FlatButton>
                                    <strong>{workspace.workspaceName}</strong>
                                </FlatButton>
                            </EllipsisTextHint>
                        </span>
                    )}>
                    <PopoverHeader>
                        Workspace Contents
                    </PopoverHeader>
                    <WorkspaceDropdownContent workspaceDetails={workspaceDetails}/>
                </Popover>
            </Cell>
            <Cell>{workspace.workspaceDescription}</Cell>
            <Cell>{workspaceDetails?.owner?.userId === userId
                ? WorkspaceOwnershipFilterLabel.USER
                : WorkspaceOwnershipFilterLabel.SHARED}
            </Cell>
            <Cell>{lastUpdated}</Cell>
            <Cell>{created}</Cell>
            <Cell>
                {(workspaceDetails && workspaceDetails?.collaboratorsCount > 1) ? (
                    <Group alignItems="center" direction="h" gap="xxs">
                        |<Icon type={Icons.MY_GROUP}/>{workspaceDetails?.collaboratorsCount}
                    </Group>
                ) : null}
            </Cell>
        </Row>
    );
};
